<script setup lang="ts">
import { Roles } from '@/constants'
import directoriesHttpService from '@/services/http/directoriesHttpService'
import type { InstitutionModel } from '@/services/http/httpResponses'
import { ref } from 'vue'

definePage({
  meta: { allowedRoles: [Roles.Admin] },
})

const INSTITUTION_TYPES: Record<number, string> = {
  0: 'Колледж',
  1: 'Университет',
}

const items = ref<InstitutionModel[]>([])
const totalCount = ref(0)
const loading = ref(false)
const search = ref('')
const city = ref('')
const page = ref(1)
const PAGE_SIZE = 50

const load = async () => {
  loading.value = true
  try {
    const res = await directoriesHttpService.getInstitutions({
      search: search.value || undefined,
      city: city.value || undefined,
      page: page.value,
      pageSize: PAGE_SIZE,
    })
    items.value = res.items
    totalCount.value = res.totalCount
  } finally {
    loading.value = false
  }
}

const onSearch = () => {
  page.value = 1
  load()
}

const onReset = () => {
  search.value = ''
  city.value = ''
  onSearch()
}

const onPage = (event: { page: number }) => {
  page.value = event.page + 1
  load()
}

await load()
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-semibold">Учреждения</h2>
      <span class="text-gray-500 text-sm">Всего: {{ totalCount }}</span>
    </div>

    <div class="flex gap-2 flex-wrap">
      <InputText
        v-model="search"
        placeholder="Поиск по названию"
        class="w-80"
        @keyup.enter="onSearch"
      />
      <InputText v-model="city" placeholder="Город" class="w-40" @keyup.enter="onSearch" />
      <Button label="Найти" @click="onSearch" />
      <Button v-if="search || city" label="Сбросить" severity="secondary" text @click="onReset" />
    </div>

    <DataTable :value="items" :loading="loading" dataKey="id">
      <Column field="title" header="Название" />
      <Column field="city" header="Город" style="width: 150px" />
      <Column header="Тип" style="width: 120px">
        <template #body="{ data }">
          {{ INSTITUTION_TYPES[data.type] ?? data.type }}
        </template>
      </Column>
      <Column header="Сайт" style="width: 200px">
        <template #body="{ data }">
          <a
            v-if="data.webSite"
            :href="data.webSite"
            target="_blank"
            class="text-blue-500 hover:underline text-sm truncate block max-w-[180px]"
          >
            {{ data.webSite }}
          </a>
          <span v-else class="text-gray-400">—</span>
        </template>
      </Column>
      <Column header="Кампус / Военная кафедра" style="width: 180px">
        <template #body="{ data }">
          <div class="flex gap-1">
            <Tag v-if="data.hasCampus" value="Кампус" severity="info" />
            <Tag v-if="data.hasMilitaryDepartment" value="Воен. каф." severity="secondary" />
          </div>
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col gap-4 items-center py-4">
          <span class="pi pi-building text-6xl text-gray-300" />
          <p class="text-gray-500">Данные отсутствуют</p>
        </div>
      </template>
    </DataTable>

    <Paginator
      v-if="totalCount > PAGE_SIZE"
      :rows="PAGE_SIZE"
      :totalRecords="totalCount"
      :first="(page - 1) * PAGE_SIZE"
      @page="onPage"
    />
  </section>
</template>

<style scoped></style>
